import {
  Center,
  Code,
  Container,
  EmptyState,
  HStack,
  IconButton,
  Skeleton,
  Stack,
  Table,
} from "@chakra-ui/react";
import * as React from "react";
import {
  LuDownload,
  LuEllipsis,
  LuFile,
  LuImage,
  LuInfo,
  LuPencil,
  LuStar,
  LuTrash,
} from "react-icons/lu";

import { PatreonBenefit, getWorkspacePatreonCampaign } from "../api/patreon";
import {
  Upload,
  UploadWithStats,
  createWorkspaceUpload,
  deleteWorkspaceUpload,
  getWorkspaceUploadBenefits,
  getWorkspaceUploadDownloadUrl,
  getWorkspaceUploads,
  patchWorkspaceUpload,
  updateWorkspaceUploadBenefits,
} from "../api/uploads";
import { Workspace } from "../api/workspaces";
import { BenefitsModal } from "../components/benefits-modal";
import {
  MenuContent,
  MenuItem,
  MenuRoot,
  MenuTrigger,
} from "../components/chakra/menu";
import { Tooltip } from "../components/chakra/tooltip";
import {
  FileDialog,
  FileDialogMode,
  FileFormData,
} from "../components/file-dialog";
import { FormatBytes } from "../components/format-bytes";
import { SectionHeader } from "../components/section-header";
import { useLoading, useTitle, useToaster, useWorkspace } from "../hooks";
import { WorkspaceLayout } from "../layouts/workspace";
import { rewriteCdnUrl } from "../lib/cdn";

export const FilesPage = () => {
  useTitle("Files");
  const workspace = useWorkspace()!;
  const [uploadDialogOpen, setUploadDialogOpen] = React.useState(false);
  const [uploadDialogLoading, withUploadDialogLoading] = useLoading();
  const withToast = useToaster();
  const [uploads, setUploads] = React.useState<UploadWithStats[]>([]);
  const [isLoading, withLoading] = useLoading(true);

  const loadUploads = React.useCallback(async () => {
    await withToast(async () => {
      await withLoading(async () => {
        const uploads = await getWorkspaceUploads(workspace);
        setUploads(uploads);
      });
    });
  }, [withLoading, withToast, workspace]);

  React.useEffect(() => {
    loadUploads();
  }, [loadUploads]);

  const handleUploadFile = React.useCallback(
    async (data: FileFormData, onProgress?: (progress: number) => void) => {
      if (!data.file) throw new Error("File is required");
      await withUploadDialogLoading(async () => {
        await withToast(async () => {
          await createWorkspaceUpload(
            workspace,
            {
              file: data.file!,
              title: data.title,
              description: data.description,
              thumbnails: data.thumbnails,
            },
            onProgress,
          );
          setUploadDialogOpen(false);
          loadUploads();
        });
      });
    },
    [withUploadDialogLoading, withToast, workspace, loadUploads],
  );

  return (
    <WorkspaceLayout>
      <Container maxW="4xl" pt="8">
        <Stack>
          <Stack direction="row" justify="space-between" align="center">
            <SectionHeader
              title="Files"
              subtitle="Manage files uploaded to your workspace."
            />
            <FileDialog
              mode={FileDialogMode.Upload}
              buttonSize="sm"
              isLoading={uploadDialogLoading}
              open={uploadDialogOpen}
              onOpenChange={setUploadDialogOpen}
              onSave={handleUploadFile}
            />
          </Stack>
          {isLoading || uploads.length > 0 ? (
            <FilesTable
              isLoading={isLoading}
              onRefresh={loadUploads}
              uploads={uploads}
              workspace={workspace}
            />
          ) : (
            <EmptyFilesState onRefresh={loadUploads} />
          )}
        </Stack>
      </Container>
    </WorkspaceLayout>
  );
};

interface EmptyFilesStateProps {
  onRefresh: () => void;
}

const EmptyFilesState = ({ onRefresh }: EmptyFilesStateProps) => {
  const workspace = useWorkspace()!;
  const [uploadDialogOpen, setUploadDialogOpen] = React.useState(false);
  const [uploadDialogLoading, withUploadDialogLoading] = useLoading();
  const withToast = useToaster();

  const handleUploadFile = React.useCallback(
    async (data: FileFormData, onProgress?: (progress: number) => void) => {
      await withUploadDialogLoading(async () => {
        await withToast(async () => {
          if (!data.file) throw new Error("File is required");
          await createWorkspaceUpload(
            workspace,
            {
              file: data.file,
              title: data.title,
              description: data.description,
              thumbnails: data.thumbnails,
            },
            onProgress,
          );
          setUploadDialogOpen(false);
          onRefresh();
        });
      });
    },
    [withUploadDialogLoading, withToast, workspace, onRefresh],
  );

  return (
    <EmptyState.Root mt="24">
      <EmptyState.Content>
        <EmptyState.Indicator>
          <LuFile />
        </EmptyState.Indicator>
        <Stack textAlign="center">
          <EmptyState.Title>No Files Yet</EmptyState.Title>
          <EmptyState.Description>
            Upload files that your patrons can access.
          </EmptyState.Description>
        </Stack>
        <FileDialog
          mode={FileDialogMode.Upload}
          buttonSize="xs"
          open={uploadDialogOpen}
          onOpenChange={setUploadDialogOpen}
          isLoading={uploadDialogLoading}
          onSave={handleUploadFile}
        />
      </EmptyState.Content>
    </EmptyState.Root>
  );
};

interface FilesTableProps {
  uploads: UploadWithStats[];
  workspace: Workspace;
  onRefresh: () => void;
  isLoading?: boolean;
}

const FilesTable = ({
  uploads,
  workspace,
  onRefresh,
  isLoading,
}: FilesTableProps) => {
  const [benefitsModalOpen, setBenefitsModalOpen] = React.useState(false);
  const [editModalOpen, setEditModalOpen] = React.useState(false);
  const [selectedFile, setSelectedFile] = React.useState<Upload | null>(null);
  const [selectedBenefits, setSelectedBenefits] = React.useState<string[]>([]);
  const [benefits, setBenefits] = React.useState<PatreonBenefit[]>([]);
  const [isLoadingBenefits, withLoadingBenefits] = useLoading();
  const [isUpdating, withUpdating] = useLoading();
  const withToast = useToaster();

  const handleEdit = React.useCallback((file: Upload) => {
    setSelectedFile(file);
    setEditModalOpen(true);
  }, []);

  const handleManageBenefits = React.useCallback((file: Upload) => {
    setSelectedFile(file);
    setBenefitsModalOpen(true);
  }, []);

  React.useEffect(() => {
    const loadBenefits = async () => {
      if (!benefitsModalOpen || !selectedFile) {
        return;
      }

      await withToast(async () => {
        await withLoadingBenefits(async () => {
          const [workspaceBenefits, currentBenefits] = await Promise.all([
            (await getWorkspacePatreonCampaign(workspace.id)).benefits,
            getWorkspaceUploadBenefits(workspace, selectedFile.id),
          ]);
          setBenefits(workspaceBenefits);
          setSelectedBenefits(
            currentBenefits.map((b) => b["benefit-external-id"]),
          );
        });
      });
    };

    loadBenefits();
  }, [
    benefitsModalOpen,
    selectedFile,
    withLoadingBenefits,
    withToast,
    workspace,
  ]);

  const handleSaveBenefits = () => {
    withToast(async () => {
      if (selectedFile) {
        await updateWorkspaceUploadBenefits(
          workspace,
          selectedFile.id,
          selectedBenefits,
        );
      }
      setBenefitsModalOpen(false);
      setSelectedFile(null);
    });
  };

  const handleSaveEdit = React.useCallback(
    async (data: FileFormData, onProgress?: (progress: number) => void) => {
      if (!selectedFile) return;
      await withUpdating(async () => {
        await withToast(async () => {
          await patchWorkspaceUpload(
            workspace,
            selectedFile.id,
            {
              title: data.title,
              description: data.description,
              file: data.file,
              thumbnails: data.thumbnails,
            },
            onProgress,
          );
          setEditModalOpen(false);
          setSelectedFile(null);
          onRefresh();
        });
      });
    },
    [selectedFile, onRefresh, withToast, withUpdating, workspace],
  );

  return (
    <>
      {selectedFile && (
        <>
          <BenefitsModal
            isOpen={benefitsModalOpen}
            onClose={() => {
              setBenefitsModalOpen(false);
              setSelectedFile(null);
            }}
            onSave={handleSaveBenefits}
            benefits={benefits}
            selectedBenefits={selectedBenefits}
            setSelectedBenefits={setSelectedBenefits}
            isLoading={isLoadingBenefits}
            fileName={selectedFile.title}
          />
          <FileDialog
            file={selectedFile}
            isLoading={isUpdating}
            mode={FileDialogMode.Edit}
            onOpenChange={setEditModalOpen}
            onSave={handleSaveEdit}
            open={editModalOpen}
          />
        </>
      )}
      <Table.Root>
        <Table.Header>
          <Table.Row>
            <Table.ColumnHeader
              display={{ base: "none", md: "table-cell" }}
            ></Table.ColumnHeader>
            <Table.ColumnHeader>Title</Table.ColumnHeader>
            <Table.ColumnHeader display={{ base: "none", md: "table-cell" }}>
              File Name
            </Table.ColumnHeader>
            <Table.ColumnHeader display={{ base: "none", md: "table-cell" }}>
              Size
            </Table.ColumnHeader>
            <Table.ColumnHeader>
              <HStack gap="1">
                <span>Patrons</span>
                <Tooltip
                  content="Number of patrons with access to this file"
                  showArrow
                >
                  <LuInfo size="14" />
                </Tooltip>
              </HStack>
            </Table.ColumnHeader>
            <Table.ColumnHeader>Benefits</Table.ColumnHeader>
            <Table.ColumnHeader width="10"></Table.ColumnHeader>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {isLoading
            ? [...Array(3)].map((_, i) => (
                <Table.Row key={i}>
                  <Table.Cell
                    display={{ base: "none", md: "table-cell" }}
                    width="60px"
                  >
                    <Skeleton height="40px" width="40px" />
                  </Table.Cell>
                  <Table.Cell>
                    <Skeleton height="20px" />
                  </Table.Cell>
                  <Table.Cell display={{ base: "none", md: "table-cell" }}>
                    <Skeleton height="20px" />
                  </Table.Cell>
                  <Table.Cell display={{ base: "none", md: "table-cell" }}>
                    <Skeleton height="20px" />
                  </Table.Cell>
                  <Table.Cell>
                    <Skeleton height="20px" />
                  </Table.Cell>
                  <Table.Cell>
                    <Skeleton height="20px" />
                  </Table.Cell>
                  <Table.Cell>
                    <Skeleton height="20px" width="32px" />
                  </Table.Cell>
                </Table.Row>
              ))
            : uploads.map((upload) => (
                <FileRow
                  key={upload.id}
                  file={upload}
                  workspace={workspace}
                  onDelete={onRefresh}
                  onManageBenefits={handleManageBenefits}
                  onEdit={handleEdit}
                />
              ))}
        </Table.Body>
      </Table.Root>
    </>
  );
};

interface FileRowProps {
  file: UploadWithStats;
  onDelete: () => void;
  onEdit: (file: Upload) => void;
  onManageBenefits: (file: Upload) => void;
  workspace: Workspace;
}

enum FileAction {
  Delete = "delete",
  Download = "download",
  ManageBenefits = "manage-benefits",
  Edit = "edit",
}

const FileRow = (props: FileRowProps) => {
  const { file, onDelete, onEdit, onManageBenefits, workspace } = props;
  const withToast = useToaster();
  const [isDeleting, withDeleting] = useLoading();

  const handleAction = React.useCallback(
    async (action: FileAction) => {
      switch (action) {
        case FileAction.Delete:
          if (confirm(`Delete ${file.title}?`)) {
            await withDeleting(async () => {
              await withToast(async () => {
                await deleteWorkspaceUpload(workspace, file.id);
                onDelete();
              });
            });
          }
          return;
        case FileAction.Download:
          {
            const url = getWorkspaceUploadDownloadUrl(workspace, file.id);
            window.open(url, "_blank");
          }
          return;
        case FileAction.ManageBenefits:
          onManageBenefits(file);
          return;
        case FileAction.Edit:
          onEdit(file);
          return;
      }
    },
    [
      file,
      onDelete,
      withToast,
      withDeleting,
      workspace,
      onManageBenefits,
      onEdit,
    ],
  );

  return (
    <Table.Row>
      <Table.Cell display={{ base: "none", md: "table-cell" }} width="60px">
        {file.thumbnails?.[0] ? (
          <img
            src={rewriteCdnUrl(
              `${file.thumbnails[0]}-/format/auto/-/scale_crop/80x80/center/`,
            )}
            alt={`Thumbnail for ${file.title}`}
            style={{
              width: 40,
              height: 40,
              objectFit: "cover",
              borderRadius: "8px",
            }}
          />
        ) : (
          <Center
            w="40px"
            h="40px"
            bg="gray.100"
            borderRadius="8px"
            color="gray.400"
          >
            <LuImage size={16} />
          </Center>
        )}
      </Table.Cell>
      <Table.Cell>{file.title}</Table.Cell>
      <Table.Cell display={{ base: "none", md: "table-cell" }}>
        <Code>{file["original-filename"]}</Code>
      </Table.Cell>
      <Table.Cell display={{ base: "none", md: "table-cell" }}>
        <FormatBytes value={file.size} />
      </Table.Cell>
      <Table.Cell fontVariantNumeric="tabular-nums">{file.patrons}</Table.Cell>
      <Table.Cell>
        {file.benefits && file.benefits.length > 0 ? (
          <HStack gap="1">
            <LuStar size="14" />
            <span>{file.benefits.length}</span>
          </HStack>
        ) : (
          <span>&mdash;</span>
        )}
      </Table.Cell>
      <Table.Cell>
        <MenuRoot onSelect={(data) => handleAction(data.value as FileAction)}>
          <MenuTrigger asChild>
            <IconButton
              aria-label="Options"
              size="sm"
              borderRadius="full"
              variant="ghost"
              disabled={isDeleting}
            >
              <LuEllipsis />
            </IconButton>
          </MenuTrigger>
          <MenuContent>
            <MenuItem value={FileAction.Edit}>
              <LuPencil />
              Edit
            </MenuItem>
            <MenuItem value={FileAction.Download}>
              <LuDownload />
              Download
            </MenuItem>
            <MenuItem value={FileAction.ManageBenefits}>
              <LuStar />
              Benefits
            </MenuItem>
            <MenuItem value={FileAction.Delete}>
              <LuTrash />
              Delete
            </MenuItem>
          </MenuContent>
        </MenuRoot>
      </Table.Cell>
    </Table.Row>
  );
};
